// src/pages/po/Requisicoes.tsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTableList, faClipboardList, faClipboardCheck, faBusinessTime, faBellConcierge, faList, faListCheck } from '@/icons';
import KanbanColumn from '../../components/common/KanbanColumn';
import KanbanCard from '../../components/common/KanbanCard';
import SidePanel from '../../components/common/SidePanel';
import ContactItemsSidePanelContent from './ContactItemsSidePanelContent';
import RequisicaoItemsSidePanelContent from './RequisicaoItemsSidePanelContent';
import AprovacaoItemsSidePanelContent from './AprovacaoItemsSidePanelContent';
import AdvancedSearchRequestsKanbanModalContent from './AdvancedSearchRequestsKanbanModalContent';
import { useTheme } from '../../contexts/ThemeContext';
import { Contato } from '../../types/contatos/Contato';
import { Requisicao } from '../../types/compras/Requisicao';
import './Requisicoes.css';
import { formatDateTime } from '../../utils/formatUtils';
import { useAuth } from '../../contexts/AuthContext';
import IconSearchButton from '../../components/common/IconSearchButton';
import IconResetButton from '../../components/common/IconResetButton';
import Modal from '../../components/common/Modal';
import { useRequisicoesKanban } from '../../hooks/useRequisicoesKanban';

const Requisicoes = () => {
  const { theme } = useTheme();
  const { hasPermission } = useAuth();
  const kanbans = useRequisicoesKanban();
  const [showContatoItemsPanel, setShowContatoItemsPanel] = useState(false);
  const [showRequisicaoItemsPanel, setShowRequisicaoItemsPanel] = useState(false);
  const [showAprovacaoPanel, setShowAprovacaoPanel] = useState(false);
  const [selectedContato, setSelectedContato] = useState<Contato | null>(null);
  const [selectedRequisicao, setSelectedRequisicao] = useState<Requisicao | null>(null);
  const [selectedRequisicaoAprovar, setSelectedRequisicaoAprovar] = useState<Requisicao | null>(null);
  const [itemFilter, setItemFilter] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const contatosSentinelRef = useRef<HTMLDivElement>(null);
  const aprovarSentinelRef = useRef<HTMLDivElement>(null);
  const pendentesSentinelRef = useRef<HTMLDivElement>(null);
  const emProcessoSentinelRef = useRef<HTMLDivElement>(null);
  const finalizadasSentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Bluelt | Requisições';
  }, []);

  useEffect(() => {
    if (kanbans.contatos.items.length === 0 || !kanbans.contatos.hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.contatos.isLoading) {
          kanbans.contatos.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = contatosSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.contatos.items, kanbans.contatos.hasMore, kanbans.contatos.isLoading, kanbans.contatos.loadMore]);

  useEffect(() => {
    if (kanbans.aprovar.items.length === 0 || !kanbans.aprovar.hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.aprovar.isLoading) {
          kanbans.aprovar.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = aprovarSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.aprovar.items, kanbans.aprovar.hasMore, kanbans.aprovar.isLoading, kanbans.aprovar.loadMore]);

  useEffect(() => {
    if (kanbans.pendentes.items.length === 0 || !kanbans.pendentes.hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.pendentes.isLoading) {
          kanbans.pendentes.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = pendentesSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.pendentes.items, kanbans.pendentes.hasMore, kanbans.pendentes.isLoading, kanbans.pendentes.loadMore]);

  useEffect(() => {
    if (kanbans.emProcesso.items.length === 0 || !kanbans.emProcesso.hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.emProcesso.isLoading) {
          kanbans.emProcesso.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = emProcessoSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.emProcesso.items, kanbans.emProcesso.hasMore, kanbans.emProcesso.isLoading, kanbans.emProcesso.loadMore]);

  useEffect(() => {
    if (kanbans.finalizadas.items.length === 0 || !kanbans.finalizadas.hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.finalizadas.isLoading) {
          kanbans.finalizadas.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = finalizadasSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.finalizadas.items, kanbans.finalizadas.hasMore, kanbans.finalizadas.isLoading, kanbans.finalizadas.loadMore]);

  // useCallbacks declarados antes do early-return de permissao para satisfazer
  // regras de hooks (ADR-010 — fix do bug latente do APP-BLUELTSYSTEM-1 que
  // dispara se hasPermission mudar runtime no mesmo mount).
  const handleViewContatoItemsClick = useCallback((contato: Contato) => {
    setSelectedContato(contato);
    setShowContatoItemsPanel(true);
  }, []);

  const handleViewRequisicaoItemsClick = useCallback((requisicao: Requisicao, filter: string) => {
    setSelectedRequisicao(requisicao);
    setItemFilter(filter);
    setShowRequisicaoItemsPanel(true);
  }, []);

  const handleViewAprovacaoClick = useCallback((requisicao: Requisicao) => {
    setSelectedRequisicaoAprovar(requisicao);
    setShowAprovacaoPanel(true);
  }, []);

  const renderContatoItem = useCallback((contato: Contato) => (
    <KanbanCard<Contato>
      item={contato}
      renderContent={(c) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <strong>Contato {c.nroContato}</strong>
          <div>Total de Itens: {c.qtdItens}</div>
          <div>Filial: {c.nomeFantasia || 'Erro de Parametrização'}</div>
          <div>Consultor: {c.nomeUsuario || 'Erro de Integração'}</div>
          <div>Cliente: {c.nomeCliente || 'Erro de Integração'}</div>
          <div>
            {c.countDiasAberto === 0
              ? 'Aberto hoje'
              : c.countDiasAberto < 0
                ? 'Dias em Aberto: Erro'
                : `Aberto há ${c.countDiasAberto} dia${c.countDiasAberto === 1 ? '' : 's'}`}
          </div>
        </div>
      )}
      isDraggable={false}
      actionButton={
        contato.valido
          ? {
              icon: faTableList,
              onClick: handleViewContatoItemsClick,
              title: 'Visualizar Detalhes',
              className: 'list-itens-card list-itens-card-contatos',
            }
          : undefined
      }
      isValid={contato.valido}
    />
  ), [handleViewContatoItemsClick]);

  const renderAprovarItem = useCallback((requisicao: Requisicao) => (
    <KanbanCard<Requisicao>
      item={requisicao}
      renderContent={(r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <strong>Requisição {r.id}</strong>
          {Object.entries(r.itensCount).map(([tipo, quantidade]: [string, number]) => (
            <div key={tipo}>Tipo: {tipo} | Quantidade itens: {quantidade}</div>
          ))}
          {typeof r.somaCustoTotalAprovar === 'number' && (
            <div>
              <strong>Total a aprovar: {r.somaCustoTotalAprovar.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
            </div>
          )}
          {r.existDevolucao && <strong>Existem devoluções na Requisição!</strong>}
          {r.existReprovado && <strong>Existem reprovados na Requisição!</strong>}
          <div>
            {!r.nroContato && r.origem === 'PLANEJAMENTO'
              ? 'Solicitação Planejamento'
              : `Contato ${r.nroContato}`} | Filial: {r.nomeFantasia}
          </div>
          <div>Requisitante: {r.nomeUsuario}</div>
          {r.origem !== 'PLANEJAMENTO' && <div>Cliente: {r.nomeCliente}</div>}
          {r.origem !== 'PLANEJAMENTO' && <div>Consultor: {r.nomeUsuarioConsultor}</div>}
          <div>
            {r.countDiasAberto === 0
              ? 'Aberto hoje'
              : r.countDiasAberto < 0
                ? 'Dias em Aberto: Erro'
                : `Aberto há ${r.countDiasAberto} dia${r.countDiasAberto === 1 ? '' : 's'}`}
          </div>
        </div>
      )}
      isDraggable={false}
      actionButton={{
        icon: faTableList,
        onClick: () => handleViewAprovacaoClick(requisicao),
        title: 'Visualizar Detalhes',
        className: 'list-itens-card list-itens-card-requisicoes-aprovar',
      }}
      isValid={!requisicao.existDevolucao && !requisicao.existReprovado}
    />
  ), [handleViewAprovacaoClick]);

  const renderPendenteItem = useCallback((requisicao: Requisicao) => (
      <KanbanCard<Requisicao>
          item={requisicao}
          renderContent={(r) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <strong>Requisição {r.id}</strong>
                  {Object.entries(r.itensCount).map(([tipo, quantidade]: [string, number]) => (
                      <div key={tipo}>Tipo: {tipo} | Quantidade itens: {quantidade}</div>
                  ))}
                  {r.existDevolucao && <strong>Existem devoluções na Requisição!</strong>}
                  {r.existReprovado && <strong>Existem reprovados na Requisição!</strong>}
                  <div>
                      {!r.nroContato && r.origem === 'PLANEJAMENTO'
                          ? 'Solicitação Planejamento'
                          : `Contato ${r.nroContato}`} | Filial: {r.nomeFantasia}
                  </div>
                  <div>Requisitante: {r.nomeUsuario}</div>
                  {r.origem !== 'PLANEJAMENTO' && <div>Cliente: {r.nomeCliente}</div>}
                  {r.origem !== 'PLANEJAMENTO' && <div>Consultor: {r.nomeUsuarioConsultor}</div>}
                  <div>
                      {r.countDiasAberto === 0
                          ? 'Aberto hoje'
                          : r.countDiasAberto < 0
                          ? 'Dias em Aberto: Erro'
                          : `Aberto há ${r.countDiasAberto} dia${r.countDiasAberto === 1 ? '' : 's'}`}
                  </div>
              </div>
          )}
          isDraggable={false}
          actionButton={{
              icon: faTableList,
              onClick: () => handleViewRequisicaoItemsClick(requisicao, 'pendente'),
              title: 'Visualizar Detalhes',
              className: 'list-itens-card list-itens-card-requisicoes-pendente',
          }}
          isValid={!requisicao.existDevolucao && !requisicao.existReprovado}
      />
  ), [handleViewRequisicaoItemsClick]);

  const renderEmProcessoItem = useCallback((requisicao: Requisicao) => (
      <KanbanCard<Requisicao>
          item={requisicao}
          renderContent={(r) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <strong>Requisição {r.id}</strong>
                  {Object.entries(r.itensCount).map(([tipo, quantidade]: [string, number]) => (
                      <div key={tipo}>Tipo: {tipo} | Quantidade itens: {quantidade}</div>
                  ))}
                  {r.existDevolucao && <strong>Existem devoluções na Requisição!</strong>}
                  {r.existReprovado && <strong>Existem reprovados na Requisição!</strong>}
                  <div>
                      {!r.nroContato && r.origem === 'PLANEJAMENTO'
                          ? 'Solicitação Planejamento'
                          : `Contato ${r.nroContato}`} | Filial: {r.nomeFantasia}
                  </div>
                  <div>Requisitante: {r.nomeUsuario}</div>
                  {r.origem !== 'PLANEJAMENTO' && <div>Cliente: {r.nomeCliente}</div>}
                  {r.origem !== 'PLANEJAMENTO' && <div>Consultor: {r.nomeUsuarioConsultor}</div>}
                  <div>
                      {r.countDiasAberto === 0
                          ? 'Aberto hoje'
                          : r.countDiasAberto < 0
                          ? 'Dias em Aberto: Erro'
                          : `Aberto há ${r.countDiasAberto} dia${r.countDiasAberto === 1 ? '' : 's'}`}
                  </div>
              </div>
          )}
          isDraggable={false}
          actionButton={{
              icon: faTableList,
              onClick: () => handleViewRequisicaoItemsClick(requisicao, 'emprocesso'),
              title: 'Visualizar Detalhes',
              className: 'list-itens-card list-itens-card-requisicoes-em-processo',
          }}
          isValid={!requisicao.existDevolucao && !requisicao.existReprovado}
      />
  ), [handleViewRequisicaoItemsClick]);

  const renderFinalizadaDevolvidaItem = useCallback((requisicao: Requisicao) => (
      <KanbanCard<Requisicao>
          item={requisicao}
          renderContent={(r) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <strong>Requisição {r.id}</strong>
                  {Object.entries(r.itensCount).map(([tipo, quantidade]: [string, number]) => (
                      <div key={tipo}>Tipo: {tipo} | Quantidade itens: {quantidade}</div>
                  ))}
                  {r.existDevolucao && <strong>Existem devoluções na Requisição!</strong>}
                  {r.existReprovado && <strong>Existem reprovados na Requisição!</strong>}
                  <div>
                      {!r.nroContato && r.origem === 'PLANEJAMENTO'
                          ? 'Solicitação Planejamento'
                          : `Contato ${r.nroContato}`} | Filial: {r.nomeFantasia}
                  </div>
                  <div>Requisitante: {r.nomeUsuario}</div>
                  {r.origem !== 'PLANEJAMENTO' && <div>Cliente: {r.nomeCliente}</div>}
                  {r.origem !== 'PLANEJAMENTO' && <div>Consultor: {r.nomeUsuarioConsultor}</div>}
                  <div>Data de encerramento: {formatDateTime(r.dataEncerrada)}</div>
              </div>
          )}
          isDraggable={false}
          actionButton={{
              icon: faTableList,
              onClick: () => handleViewRequisicaoItemsClick(requisicao, 'finalizado'),
              title: 'Visualizar Detalhes',
              className: 'list-itens-card list-itens-card-requisicoes-finalizado',
          }}
          isValid={!requisicao.existDevolucao && !requisicao.existReprovado}
      />
  ), [handleViewRequisicaoItemsClick]);

  const requiredPermission = 'modulo.requisicoes';
  if (!hasPermission(requiredPermission)) {
    return <div>Você não tem permissão para acessar esta página.</div>;
  }

  const handleCloseContatoPanel = () => {
    setShowContatoItemsPanel(false);
    setSelectedContato(null);
  };

  const handleCloseRequisicaoPanel = () => {
    setShowRequisicaoItemsPanel(false);
    setSelectedRequisicao(null);
    setItemFilter(null);
    kanbans.reloadAllColumns();
  };

  const handleCloseAprovacaoPanel = () => {
    setShowAprovacaoPanel(false);
    setSelectedRequisicaoAprovar(null);
    kanbans.reloadAllColumns();
  };

  const getContatoClassName = (contato: Contato) => {
    return !contato.valido ? 'invalid-card' : '';
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleOpenSearchModal = () => {
    setIsModalOpen(true);
  };

  return (
    <div className={`requisicoes-page ${theme}`}>
      <main className="requisicoes-main">
        <h1>
          <FontAwesomeIcon icon={faClipboardList} /> Gestão de Requisições
        </h1>
        <div className="header-actions">
          {kanbans.isSearchActive && (
            <IconResetButton
              onClick={kanbans.handleResetSearch}
              title="Limpar Pesquisa"
            />
          )}
          <IconSearchButton
            onClick={handleOpenSearchModal}
            title="Pesquisar"
          />
        </div>
      </main>
      <div className="requisicoes-content">
        <div className="kanban-container">
          <KanbanColumn<Contato>
            title="Aguardando Abertura"
            icon={faBusinessTime}
            data={kanbans.contatos.items}
            renderItem={renderContatoItem}
            onDragOver={onDragOver}
            onDrop={onDrop}
            ref={contatosSentinelRef}
            emptyMessage="Nenhum contato encontrado"
            getItemClassName={getContatoClassName}
            showCountCircle={true}
            totalCount={kanbans.contatos.totalItems}
          />
          {/* Oculta a coluna quando nao ha itens a aprovar; futuramente essa visibilidade sera controlada por parametrizacao vinda do backend. */}
          {kanbans.aprovar.items.length > 0 && (
            <KanbanColumn<Requisicao>
              title="Aguardando aprovação"
              icon={faClipboardCheck}
              data={kanbans.aprovar.items}
              renderItem={renderAprovarItem}
              onDragOver={onDragOver}
              onDrop={onDrop}
              ref={aprovarSentinelRef}
              emptyMessage="Nenhuma requisição aguardando aprovação"
              showCountCircle={true}
              totalCount={kanbans.aprovar.totalItems}
            />
          )}
          <KanbanColumn<Requisicao>
            title="Com Itens Pendentes"
            icon={faBellConcierge}
            data={kanbans.pendentes.items}
            renderItem={renderPendenteItem}
            onDragOver={onDragOver}
            onDrop={onDrop}
            ref={pendentesSentinelRef}
            emptyMessage="Nenhuma requisição encontrada"
            showCountCircle={true}
            totalCount={kanbans.pendentes.totalItems}
          />
          <KanbanColumn<Requisicao>
            title="Com Itens em Processo"
            icon={faList}
            data={kanbans.emProcesso.items}
            renderItem={renderEmProcessoItem}
            onDragOver={onDragOver}
            onDrop={onDrop}
            ref={emProcessoSentinelRef}
            emptyMessage="Nenhuma requisição encontrada"
            showCountCircle={true}
            totalCount={kanbans.emProcesso.totalItems}
          />
          <KanbanColumn<Requisicao>
            title="Com Itens Finalizados"
            icon={faListCheck}
            data={kanbans.finalizadas.items}
            renderItem={renderFinalizadaDevolvidaItem}
            onDragOver={onDragOver}
            onDrop={onDrop}
            ref={finalizadasSentinelRef}
            emptyMessage="Nenhuma requisição encontrada"
            showCountCircle={true}
            totalCount={kanbans.finalizadas.totalItems}
          />
        </div>
        <SidePanel isOpen={showContatoItemsPanel} onClose={handleCloseContatoPanel} theme={theme}>
          {selectedContato && (
            <ContactItemsSidePanelContent
              contato={selectedContato}
              theme={theme}
              onClose={() => {
                handleCloseContatoPanel();
                kanbans.reloadAllColumns();
              }}
            />
          )}
        </SidePanel>
        <SidePanel isOpen={showRequisicaoItemsPanel} onClose={handleCloseRequisicaoPanel} theme={theme}>
          {selectedRequisicao && (
            <RequisicaoItemsSidePanelContent
              requisicao={selectedRequisicao}
              theme={theme}
              onClose={handleCloseRequisicaoPanel}
              onDataChange={kanbans.reloadAllColumns}
              itemFilter={itemFilter}
            />
          )}
        </SidePanel>
        <SidePanel isOpen={showAprovacaoPanel} onClose={handleCloseAprovacaoPanel} theme={theme}>
          {selectedRequisicaoAprovar && (
            <AprovacaoItemsSidePanelContent
              key={selectedRequisicaoAprovar.id}
              requisicao={selectedRequisicaoAprovar}
              theme={theme}
              onClose={handleCloseAprovacaoPanel}
              onDataChange={kanbans.reloadAllColumns}
            />
          )}
        </SidePanel>
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} theme={theme}>
          <AdvancedSearchRequestsKanbanModalContent
          theme={theme}
          onClose={() => setIsModalOpen(false)}
          onSearch={(result, params) => kanbans.handleSearch(result, params)}
          />
        </Modal>
      </div>
    </div>
  );
};

export default Requisicoes;
